/**
 * Admin › Network › Launch <slug>
 *
 * Full non-technical walkthrough page for spinning up a sibling site
 * (Kerala Pulse / Food Pulse / India's) in a new Emergent chat.
 * Three-step guide with a giant "Copy prompt" button and a
 * "Download launch kit (.zip)" button.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http, API } from "@/lib/api";
import { ArrowLeft, Copy, Check, Download, ExternalLink, Loader2, Sparkles, AlertTriangle, Mail, BookOpen } from "lucide-react";

export default function AdminNetworkLaunch() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data,   setData]   = useState(null);
  const [busy,   setBusy]   = useState(true);
  const [error,  setError]  = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailingDocs, setEmailingDocs] = useState(false);
  const [docsTo, setDocsTo] = useState("");

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const { data } = await http.get(`/admin/site-configs/${slug}/launch-prompt`);
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load launch kit.");
      } finally { setBusy(false); }
    })();
  }, [slug]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.prompt);
      setCopied(true);
      toast.success("Prompt copied to clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Clipboard is unavailable — select the text manually."); }
  };

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await http.get(`/admin/site-configs/${slug}/launch-package.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-pulse-launch-kit.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Launch kit downloaded.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed."); }
    finally { setDownloading(false); }
  };

  const emailZip = async () => {
    setEmailing(true);
    try {
      const payload = emailTo.trim() ? { to: emailTo.trim() } : {};
      const { data: r } = await http.post(`/admin/site-configs/${slug}/email-launch-kit`, payload);
      if (r.status === "sent") {
        toast.success(`Launch kit emailed to ${r.recipient}`);
      } else if (r.status === "skipped") {
        toast.info("Email provider is not configured on this server — download instead.");
      } else {
        toast.error(r.error || "Email failed.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed."); }
    finally { setEmailing(false); }
  };

  const emailAgentInstructions = async () => {
    setEmailingDocs(true);
    try {
      const payload = docsTo.trim() ? { to: docsTo.trim() } : {};
      const { data: r } = await http.post(`/admin/site-configs/${slug}/email-agent-instructions`, payload);
      if (r.status === "sent") {
        toast.success(`Agent playbook emailed to ${r.recipient}`);
      } else if (r.status === "skipped") {
        toast.info("Email provider is not configured on this server.");
      } else {
        toast.error(r.error || "Email failed.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed."); }
    finally { setEmailingDocs(false); }
  };

  if (busy) return <div className="p-8 opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Preparing your launch kit…</div>;
  if (error) return (
    <div className="max-w-2xl mx-auto p-8" data-testid="launch-error">
      <button onClick={() => navigate("/admin?tab=network")} className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100 mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to Network
      </button>
      <div className="rounded-2xl border-2 border-dashed p-8 text-center" style={{ borderColor: "#B23A3A55", background: "#FEF2F2" }}>
        <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: "#B23A3A" }} />
        <div className="font-display text-xl mb-1">Launch kit unavailable</div>
        <p className="text-sm opacity-80">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="admin-network-launch">
      <button onClick={() => navigate("/admin?tab=network")} className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100 mb-4" data-testid="launch-back">
        <ArrowLeft className="w-4 h-4" /> Back to Network
      </button>

      <div className="mb-6">
        <div className="overline mb-2 inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> LAUNCH KIT</div>
        <h1 className="font-display text-4xl leading-tight">Launch <span style={{ color: "var(--wofy-orange)" }}>{data.brand_name}</span></h1>
        <p className="mt-3 text-base opacity-80">
          Three steps to spin up <a href={data.primary_domain} target="_blank" rel="noreferrer" className="underline">{data.primary_domain?.replace(/^https?:\/\//, "")}</a> in a fresh Emergent chat. No coding required — you're just copying and pasting.
        </p>
      </div>

      {/* -------- Step 1 -------- */}
      <StepCard n={1} title={`Open a new Emergent chat for ${data.brand_name}`}>
        <p className="text-sm">
          Head to your Emergent workspace and open a fresh chat for the {data.brand_name} project. (If you've already scaffolded that project, open its existing chat instead.)
        </p>
      </StepCard>

      {/* -------- Step 2 -------- */}
      <StepCard n={2} title="Copy the launch prompt and paste it as the first message">
        <p className="text-sm mb-3">
          This prompt already contains your API key — treat it like a password. Don't post it publicly or commit it to git.
        </p>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="flex items-center justify-between px-3 py-2 border-b bg-black/[0.03]" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="text-[10px] font-mono uppercase opacity-70">Launch prompt — {data.prompt.length.toLocaleString()} characters</div>
            <button
              onClick={copy}
              className="text-xs rounded-full px-3 py-1.5 font-medium inline-flex items-center gap-1.5"
              style={{ background: copied ? "#065F46" : "#B25D38", color: "white" }}
              data-testid="launch-copy-prompt"
            >
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy prompt</>}
            </button>
          </div>
          <pre
            className="text-[11px] font-mono p-3 overflow-x-auto whitespace-pre-wrap max-h-72 overflow-y-auto"
            style={{ background: "#0F172A", color: "#E5E7EB" }}
            data-testid="launch-prompt-preview"
          >{data.prompt}</pre>
        </div>
      </StepCard>

      {/* -------- Step 3 -------- */}
      <StepCard n={3} title="Wait ~10 minutes while Emergent scaffolds the site">
        <p className="text-sm">
          Emergent will fetch your design tokens, brand config, and content from Social Pulse and build the whole site around them. When it's done, visit <a href={data.primary_domain} target="_blank" rel="noreferrer" className="underline font-medium">{data.primary_domain?.replace(/^https?:\/\//, "")}</a> to see it live.
        </p>
        <div className="mt-4 rounded-lg border-l-4 p-3 text-xs" style={{ borderColor: "#B25D38", background: "#FDFBF7" }}>
          <div className="font-medium mb-0.5">💡 Tip</div>
          If you want to change {data.brand_name}'s tagline, logo, palette, or feature list <b>after</b> launch, edit them in <b>Admin › Network → Edit {data.brand_name}</b> on socialpulse.com. The sibling site picks up the new values on its next page load — you never need to touch Emergent again for brand tweaks.
        </div>
      </StepCard>

      {/* -------- Alternative -------- */}
      <div className="mt-8 rounded-2xl border-2 border-dashed p-5" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display text-lg">Prefer to download a starter kit?</div>
            <p className="text-sm opacity-70 mt-1 max-w-md">
              A ZIP with the pre-filled prompt, a working Vite + React + Tailwind starter, and a README you can drop straight into an Emergent chat as an attachment.
            </p>
          </div>
          <button
            onClick={downloadZip}
            disabled={downloading}
            className="text-sm rounded-full px-4 py-2 font-medium inline-flex items-center gap-2 border-2 hover:bg-black/5 transition-colors"
            style={{ borderColor: "#B25D38", color: "#B25D38", opacity: downloading ? 0.6 : 1 }}
            data-testid="launch-download-zip"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download launch kit (.zip)
          </button>
        </div>

        {/* -------- Email option -------- */}
        <div className="mt-4 pt-4 border-t flex items-start justify-between gap-3 flex-wrap" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="min-w-0">
            <div className="font-display text-lg">Prefer to email it?</div>
            <p className="text-sm opacity-70 mt-1 max-w-md">
              We'll send the ZIP + the prompt to your own admin email. Change the address if you want it delivered somewhere else.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="you@example.com (optional)"
              className="rounded-full border-2 px-3 py-1.5 text-sm bg-transparent min-w-[220px]"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="launch-email-to"
            />
            <button
              onClick={emailZip}
              disabled={emailing}
              className="text-sm rounded-full px-4 py-2 font-medium inline-flex items-center gap-2 text-white transition"
              style={{ background: "#B25D38", opacity: emailing ? 0.6 : 1 }}
              data-testid="launch-email-btn"
            >
              {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Email launch kit
            </button>
          </div>
        </div>

        {/* -------- Email agent playbook -------- */}
        <div className="mt-4 pt-4 border-t flex items-start justify-between gap-3 flex-wrap" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="min-w-0">
            <div className="font-display text-lg">Onboarding a collaborator?</div>
            <p className="text-sm opacity-70 mt-1 max-w-md">
              Email the full <b>child-site agent playbook</b> — the same doc our Kerala Travels sibling was scaffolded from — pre-filled with this site's slug and CMS key. Recipient pastes the attached <code>.md</code> as their first message in a new Emergent chat.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="email"
              value={docsTo}
              onChange={(e) => setDocsTo(e.target.value)}
              placeholder="collaborator@example.com (optional)"
              className="rounded-full border-2 px-3 py-1.5 text-sm bg-transparent min-w-[220px]"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="launch-docs-to"
            />
            <button
              onClick={emailAgentInstructions}
              disabled={emailingDocs}
              className="text-sm rounded-full px-4 py-2 font-medium inline-flex items-center gap-2 text-white transition"
              style={{ background: "#0E7C55", opacity: emailingDocs ? 0.6 : 1 }}
              data-testid="launch-docs-btn"
            >
              {emailingDocs ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
              Email agent playbook
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center text-xs opacity-60">
        Need help? All shared logic is documented in <code>/app/HEADLESS_CMS.md</code> on socialpulse.com's server.
      </div>
    </div>
  );
}


function StepCard({ n, title, children }) {
  return (
    <div className="rounded-2xl border p-5 bg-white mb-4" style={{ borderColor: "var(--wofy-border)" }} data-testid={`launch-step-${n}`}>
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-display text-lg"
          style={{ background: "#F3EFE7", color: "#B25D38" }}
          aria-hidden
        >
          {n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg mb-2 leading-snug">{title}</div>
          {children}
        </div>
      </div>
    </div>
  );
}
